/**
 * ISP-0000 — THE FIELD TOOLBAR'S ICON COLOUR TOKEN.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Gordon, THIRD report on this one toolbar (2026-08-01): "Looks good, but the
 * color is BLACK and should be a more on-brand dark blue/gray or whatever is
 * standard on the app."
 *
 * MEASURED live on web-stg (`main.64da3626.js`, 2026-08-01, 1440px,
 * `/builder/3169`), device-pixel sampled from the rendered glyphs, with `bold`
 * carried as a known-good control so a negative could not be a broken probe:
 *
 *   emoticons         rgb(0, 0, 0)       #000000   <- the complaint
 *   specialCharacters rgb(86, 85, 86)    #565556
 *   aiAssistant       rgb(15, 82, 186)   #0f52ba
 *   bold (control)    rgb(86, 85, 86)    #565556
 *
 * So the app's standard field-toolbar icon colour is `#565556` — a warm dark
 * grey, worn by every SVG icon in the strip. `aiAssistant` is the ONE
 * deliberate exception: it is painted in the brand blue as an accent, by an
 * explicit `!important` override in `index.scss`, and it overrides the asset's
 * own `#9B5AF8` to get there. It is an accent, not the standard, and it is not
 * what the emoji should match.
 *
 * THE FINDING (stated plainly, as asked): before this change there was NO
 * token. `#565556` was a bare hex literal repeated across the codebase, and
 * `index.scss` hardcoded it again in the toolbar's `svg > path` rule. There is
 * no colours/palette/theme module in `src/` to hang it on. That is exactly how
 * the emoji drifted to black and stayed there for three rounds: nothing tied
 * the three icons to one source, so nothing could notice they had split.
 *
 * So this module introduces the token FOR ALL THREE, as a CSS custom property
 * declared once on the toolbar button. `index.scss` consumes it for the sibling
 * SVGs; `emojiToolbarButton.js` consumes the same property for the emoji. A
 * palette change now moves all of them together, and it CANNOT move one without
 * the others — which is the only kind of fix that ends this class of defect
 * rather than patching its third symptom.
 *
 * This is deliberately NOT a hardcoded hex re-stated in a second place: the
 * emoji's stylesheet references `var(--iy-field-toolbar-icon-color)`, so if the
 * token ever changes the emoji follows it without anybody remembering to.
 */

/**
 * The CSS custom property that carries the field toolbar's icon colour.
 * Declared on `button.fr-command.fr-btn` in `index.scss`, read by every icon
 * in the strip.
 */
export const FIELD_TOOLBAR_ICON_COLOR_VAR = "--iy-field-toolbar-icon-color";

/**
 * The token's value — the app's standard field-toolbar icon colour, MEASURED
 * from the sibling SVGs rather than invented. `index.scss` must declare the
 * custom property with exactly this value; `fieldToolbarIconColor.test.js`
 * asserts that, so the two cannot drift.
 */
export const FIELD_TOOLBAR_ICON_COLOR = "#565556";

/**
 * The `aiAssistant` accent, recorded here only so a reader can see that the
 * blue sparkle is a DELIBERATE exception to the token and not a second drift.
 * It is applied by `index.scss` and is intentionally left alone by this change.
 */
export const FIELD_TOOLBAR_ACCENT_COLOR = "#0f52ba";

/**
 * `var(--iy-field-toolbar-icon-color, #565556)` — the token with its measured
 * fallback, for use in a stylesheet string. The fallback matters because the
 * emoji's CSS is injected into `<head>` by JS and can, in principle, land
 * before the SCSS bundle that declares the property.
 */
export const FIELD_TOOLBAR_ICON_COLOR_CSS_VALUE = `var(${FIELD_TOOLBAR_ICON_COLOR_VAR}, ${FIELD_TOOLBAR_ICON_COLOR})`;
